import React from 'react';
import { MessageList, ConversationSkeleton } from '@ideoz/ui';
import type { Message, MessageArtifact } from '@ideoz/core';
import { useConversation } from '../context/ConversationContext';
import type { ConversationFindings } from '../hooks/useConversationFindings';

/**
 * The message-display area of the conversation workspace (extracted from
 * `ConversationModule` in Phase 2, slice 2): the loading skeleton, the scroll
 * container, and the `MessageList` itself (with its inline-finding anchors and
 * live prototyper/workbench slots).
 *
 * Presentational. Everything on `facade.streaming`/`facade.headers` and the
 * workbench-stream values are read straight from `useConversation()`; only the
 * genuinely module-owned values (the findings hook instance, the scroll ref,
 * and the module's derived flags/handlers) arrive as props. The composer — and
 * with it the interactive-question card and the scroll button — stays a sibling
 * in the module, so `useScrollToBottom` also stays there and we only receive its
 * container ref here.
 */
// Prop types mirror the matching `MessageList` props (this pane forwards them
// straight through). They are declared locally rather than `Pick`ed from
// `MessageListProps` because `@ideoz/ui` resolves to its built declarations for
// the web project, and that surface doesn't re-export the props interface.
export interface MessagesPaneProps {
  /** The single findings-state instance owned by ConversationModule. */
  findings: ConversationFindings;
  /** Scroll container ref — `useScrollToBottom` lives in the module (shared with the composer's scroll button). */
  conversationContainerRef: React.RefObject<HTMLDivElement>;
  /** While switching/loading, the messages are removed from layout and the skeleton shows. */
  showConversation: boolean;
  messages: Message[];
  projectId?: string | null;
  isLoading: boolean;
  isStreaming: boolean;
  isClarification: boolean;
  isReflection: boolean;
  isRepeated: boolean;
  isReadyToCreate: boolean;
  isPrototyper?: boolean;
  shouldShowBanner: boolean;
  isPrototypeOpen?: boolean;
  onPrototypeReturn?: () => void;
  onPrototyperClick?: (
    prototyperDemoLinkId?: string | number,
    meta?: { llmInteractionId?: string | null }
  ) => void;
  onStreamedArtifactOpen?: (artifact: MessageArtifact) => void;
  onSubmit: (text: string) => void;
  onFeedback?: (
    type: 'like' | 'dislike',
    messageId?: string | null,
    projectId?: string | null
  ) => void | Promise<void>;
  onRetryDrawing?: (drawType: string) => void;
}

export function MessagesPane({
  findings,
  conversationContainerRef,
  showConversation,
  messages,
  projectId,
  isLoading,
  isStreaming,
  isClarification,
  isReflection,
  isRepeated,
  isReadyToCreate,
  isPrototyper,
  shouldShowBanner,
  isPrototypeOpen,
  onPrototypeReturn,
  onPrototyperClick,
  onStreamedArtifactOpen,
  onSubmit,
  onFeedback,
  onRetryDrawing,
}: MessagesPaneProps): JSX.Element {
  const { facade, workbench } = useConversation();
  const { inlineFindingsByMessageId, resolvingFindingId, handleFindingAction } = findings;

  return (
    <>
      {/* Skeleton Loading */}
      {!showConversation && (
        <div className="conversation-container flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden justify-center w-full mb-4 [scrollbar-gutter:stable] scrollbar-chat px-3">
          <ConversationSkeleton messageCount={1} />
        </div>
      )}

      {/* Messages Container */}
      <div
        ref={conversationContainerRef}
        data-testid="chat-messages-container"
        className={`conversation-container flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden justify-center w-full mb-4 [scrollbar-gutter:stable] scrollbar-chat px-3 ${
          // While switching/loading, remove the previous
          // conversation from layout (not just opacity-0) so its
          // tall height + scrollbar don't linger under the
          // skeleton. It reappears once the new messages load.
          showConversation ? 'opacity-100' : 'hidden'
        }`}
      >
        <div className="min-w-0 max-w-full">
          <MessageList
            messages={messages}
            projectId={projectId}
            isLoading={isLoading}
            isStreaming={isStreaming}
            isClarification={isClarification || Boolean(facade.streaming.clarification)}
            clarificationOptions={facade.streaming.clarification?.options ?? []}
            streamProgress={facade.streaming.streamProgress}
            streamThoughts={facade.streaming.streamThoughts}
            hasStartedResponse={facade.streaming.hasStartedResponse}
            thinkingDurationMs={facade.streaming.thinkingDurationMs}
            isReflection={isReflection}
            isRepeated={isRepeated}
            isReadyToCreate={isReadyToCreate}
            isPrototyper={isPrototyper}
            shouldShowBanner={shouldShowBanner}
            onPrototypeReturn={onPrototypeReturn}
            onPrototyperClick={onPrototyperClick}
            onStreamedArtifactOpen={onStreamedArtifactOpen}
            onSubmit={onSubmit}
            onFeedback={onFeedback}
            setIsReflection={facade.headers.setIsReflection}
            setIsRepeated={facade.headers.setIsRepeated}
            onRetryDrawing={onRetryDrawing}
            // Direction is decided per message from its own
            // content/detectedLanguage (MessageItem's
            // `computedIsRtl`). Passing a single conversation-
            // wide `forcedDirection` would flip every message
            // to the same direction — an Arabic reply and an
            // English reply must render rtl/ltr independently.
            isPrototypeOpen={isPrototypeOpen}
            streamingWorkbenchSlot={
              workbench.isStreamingPrototype && isPrototypeOpen
                ? workbench.workbenchToolActivity
                : undefined
            }
            streamingWorkbenchLlmInteractionId={workbench.streamingWorkbenchLlmInteractionId}
            livePrototyperBuildTimeline={workbench.livePrototyperBuildTimeline}
            isWorkbenchBuildLive={workbench.isWorkbenchBuildLive}
            workbenchTimelineTerminalVisible={workbench.workbenchTimelineTerminalVisible}
            activeSnapshotInteractionId={workbench.activeSnapshotInteractionId}
            restoreInteractionLoadingId={workbench.restoreInteractionLoadingId}
            inlineFindingsByMessageId={inlineFindingsByMessageId}
            onFindingAction={(findingId, action) => {
              void handleFindingAction(findingId, action);
            }}
            resolvingFindingId={resolvingFindingId}
          />
        </div>
      </div>
    </>
  );
}

export default MessagesPane;
